import { Injectable } from '@angular/core';
import { HttpInterceptor, HttpRequest, HttpHandler, HttpEvent, HttpErrorResponse } from '@angular/common/http';
import { Observable, throwError, timer } from 'rxjs';
import { catchError, retry, retryWhen, mergeMap } from 'rxjs/operators';

@Injectable()
export class RetryInterceptor implements HttpInterceptor {

  // Hardcoded strings that trigger retry on 500 errors
  private readonly RETRYABLE_ERROR_MESSAGES = [
    'chosen as the deadlock victim',
    'timeout expired',
  ];

  // Maximum number of retry attempts
  private readonly MAX_RETRIES = 8;

  // Delay between retries in milliseconds
  private readonly RETRY_DELAY = 200;

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    return next.handle(request).pipe(
      retryWhen(errors =>
        errors.pipe(
          mergeMap((error, attemptNumber) => {
            // Check if this is a retryable error
            if (this.shouldRetry(error, attemptNumber)) {
              console.warn(`Retrying request (attempt ${attemptNumber + 1}/${this.MAX_RETRIES}):`, {
                url: request.url,
                method: request.method,
                error: error.error
              });

              // Calculate exponential backoff delay
              const delayMs = this.RETRY_DELAY * Math.pow(2, attemptNumber);
              return timer(delayMs);
            }

            // If not retryable, throw the error
            return throwError(error);
          })
        )
      ),
      catchError((error: HttpErrorResponse) => {
        // Final error after all retries exhausted
        return throwError(error);
      })
    );
  }

  /**
   * Determines if an error should trigger a retry
   */
  private shouldRetry(error: any, attemptNumber: number): boolean {
    // Don't retry if max attempts reached
    if (attemptNumber >= this.MAX_RETRIES) {
      return false;
    }

    // Only retry on HttpErrorResponse
    if (!(error instanceof HttpErrorResponse)) {
      return false;
    }

    // Only retry on 500 status code
    if (error.status !== 500) {
      return false;
    }

    // Check if error message contains any of the retryable strings
    const errorMessage = this.getErrorMessage(error);
    return this.containsRetryableMessage(errorMessage);
  }

  /**
   * Extracts error message from HttpErrorResponse
   */
  private getErrorMessage(error: HttpErrorResponse): string {
    let message = '';

    if (typeof error.error === 'string') {
      message = error.error;
    } else if (error.error?.Message) {
      // Handle ApiResponse format: { Success: false, Message: "error message" }
      message = error.error.Message;
    } else if (error.error?.message) {
      // Handle standard error format
      message = error.error.message;
    } else if (error.message) {
      message = error.message;
    } else if (error.statusText) {
      message = error.statusText;
    }

    return message.toLowerCase();
  }

  /**
   * Checks if error message contains any retryable strings
   */
  private containsRetryableMessage(errorMessage: string): boolean {
    return this.RETRYABLE_ERROR_MESSAGES.some(retryableMsg =>
      errorMessage.includes(retryableMsg.toLowerCase())
    );
  }
}
